import { supabase } from '@/lib/supabaseClient';

export interface ValidationResult {
  is_unique: boolean;
  message: string;
  conflict?: {
    official_id: string;
    official_name: string;
    conflict_type?: string;
    phone?: string;
    masked_rib?: string;
    in_same_league?: boolean;
  };
}

export interface BatchValidationResult {
  all_unique: boolean;
  email?: ValidationResult;
  phone?: ValidationResult;
  rib?: ValidationResult;
}

/**
 * Check if an email is unique across all officials (primary and secondary emails)
 * @param email - The email to check
 * @param officialId - Optional official ID to exclude from check (for updates)
 * @param leagueId - Optional league ID to check within specific league
 */
export async function checkEmailUniqueness(
  email: string,
  officialId?: string | null,
  leagueId?: string | null
): Promise<ValidationResult> {
  try {
    const { data, error } = await supabase.rpc('check_email_uniqueness', {
      p_email: email,
      p_official_id: officialId || null,
      p_league_id: leagueId || null
    });

    if (error) {
      console.error('Error checking email uniqueness:', error);
      return {
        is_unique: true,
        message: 'Unable to validate email'
      };
    }

    return data as ValidationResult;
  } catch (err) {
    console.error('Unexpected error checking email uniqueness:', err);
    return {
      is_unique: true,
      message: 'Validation error occurred'
    };
  }
}

/**
 * Check if a phone number is unique across all officials
 * @param phone - The phone number to check
 * @param officialId - Optional official ID to exclude from check (for updates)
 * @param leagueId - Optional league ID to check within specific league
 */
export async function checkPhoneUniqueness(
  phone: string,
  officialId?: string | null,
  leagueId?: string | null
): Promise<ValidationResult> {
  try {
    const { data, error } = await supabase.rpc('check_phone_uniqueness', {
      p_phone: phone,
      p_official_id: officialId || null,
      p_league_id: leagueId || null
    });

    if (error) {
      console.error('Error checking phone uniqueness:', error);
      return {
        is_unique: true,
        message: 'Unable to validate phone number'
      };
    }

    return data as ValidationResult;
  } catch (err) {
    console.error('Unexpected error checking phone uniqueness:', err);
    return {
      is_unique: true,
      message: 'Validation error occurred'
    };
  }
}

/**
 * Check if a RIB (bank account number) is unique across all officials
 * @param rib - The RIB to check
 * @param officialId - Optional official ID to exclude from check (for updates)
 * @param leagueId - Optional league ID to check within specific league
 */
export async function checkRibUniqueness(
  rib: string,
  officialId?: string | null,
  leagueId?: string | null
): Promise<ValidationResult> {
  try {
    const { data, error } = await supabase.rpc('check_rib_uniqueness', {
      p_rib: rib,
      p_official_id: officialId || null,
      p_league_id: leagueId || null
    });

    if (error) {
      console.error('Error checking RIB uniqueness:', error);
      return {
        is_unique: true,
        message: 'Unable to validate RIB'
      };
    }

    return data as ValidationResult;
  } catch (err) {
    console.error('Unexpected error checking RIB uniqueness:', err);
    return {
      is_unique: true,
      message: 'Validation error occurred'
    };
  }
}

/**
 * Batch validation to check uniqueness of multiple official fields at once
 * @param params - The fields to validate
 * @param officialId - Optional official ID to exclude from check (for updates)
 * @param leagueId - Optional league ID to check within specific league
 */
export async function checkOfficialUniqueness(
  params: {
    email?: string | null;
    phone?: string | null;
    rib?: string | null;
  },
  officialId?: string | null,
  leagueId?: string | null
): Promise<BatchValidationResult> {
  try {
    const { data, error } = await supabase.rpc('check_official_uniqueness', {
      p_email: params.email || null,
      p_phone: params.phone || null,
      p_rib: params.rib || null,
      p_official_id: officialId || null,
      p_league_id: leagueId || null
    });

    if (error) {
      console.error('Error checking official uniqueness:', error);
      return {
        all_unique: true
      };
    }

    return data as BatchValidationResult;
  } catch (err) {
    console.error('Unexpected error checking official uniqueness:', err);
    return {
      all_unique: true
    };
  }
}

/**
 * Validate email format
 */
export function validateEmailFormat(email: string): boolean {
  const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  return emailRegex.test(email);
}

/**
 * Validate Algerian phone number format
 */
export function validatePhoneFormat(phone: string): boolean {
  const phoneRegex = /^(\+213|0)[5-7][0-9]{8}$/;
  return phoneRegex.test(phone);
}

/**
 * Check if a secondary email conflicts with any primary email
 * @param secondaryEmails - Array of secondary emails to check
 * @param officialId - Optional official ID to exclude from check
 */
export async function checkSecondaryEmailsConflict(
  secondaryEmails: string[],
  officialId?: string | null,
  leagueId?: string | null
): Promise<{ hasConflict: boolean; conflicts: Array<{ email: string; conflict: ValidationResult }> }> {
  const conflicts: Array<{ email: string; conflict: ValidationResult }> = [];
  let hasConflict = false;

  for (const email of secondaryEmails) {
    if (email && email.trim()) {
      const result = await checkEmailUniqueness(email, officialId, leagueId);
      if (!result.is_unique) {
        hasConflict = true;
        conflicts.push({ email, conflict: result });
      }
    }
  }

  return { hasConflict, conflicts };
}